import { useCallback, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { GroupsSelector } from '@/components/GroupsSelector/GroupsSelector';
import { InputLocale } from '@/components/InputLocale/InputLocale';
import CustomFieldsGroup from '@/components/Store/CustomFieldsGroup';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import InputSearch from '@/design_system/InputSearch';
import InputText from '@/design_system/InputText';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconEdit from '@/icons/Edit.svg';
import IconTrash from '@/icons/Trash.svg';
import { formatStreet, formatZip } from '@/models/address';
import { CustomFieldsType } from '@/models/organization';
import { formatPhone } from '@/models/partials/phone';
import { StoreFromGetMany, useDeleteStore, useStores, useUpdateStore } from '@/models/store';
import { useCurrentOrganization } from '@/services/auth';
import useDebouncedState from '@/utils/useDebouncedState';

import { AddStore } from './Stores';

const STORES_PER_PAGE = 10;

export const ExternalStores = () => {
  const { t } = useLingui();
  const [page, setPage] = useState(1);
  const resetPage = useCallback(() => setPage(1), [setPage]);
  const [search, debouncedSearch, setSearch] = useDebouncedState('', 300, resetPage);

  const { data: { stores, meta } = {} } = useStores({
    limit: STORES_PER_PAGE,
    offset: (page - 1) * STORES_PER_PAGE,
    search: debouncedSearch,
    internal: false,
  });

  const noData = stores && stores.length === 0;

  return (
    <Stack gap="40px">
      <Box padding="24px" gap="24px">
        <Stack row alignItems="center" justifyContent="space-between">
          <Title size="200" as="h2">
            <Trans id="settings.external-stores.title">External stores</Trans>
          </Title>
          <AddStore external />
        </Stack>
        <InputSearch
          ariaLabel={t({ id: 'settings.external-stores.search.label', message: 'Search' })}
          placeholder={t({
            id: 'settings.external-stores.search.placeholder',
            message: 'Search by name, address, contact name, external id…',
          })}
          style={{ flex: 1, minWidth: 175, maxWidth: 500 }}
          size="medium"
          value={search}
          onChange={setSearch}
        />
        {noData && (
          <Paragraph>
            <Trans id="settings.external-stores.empty">No items for now</Trans>
          </Paragraph>
        )}
        {!noData && (
          <Table
            ariaLabel={t({
              id: 'settings.external-stores.table.label',
              message: 'External stores',
            })}
            columnWidths="minmax(200px, 1fr) minmax(200px, 1fr) minmax(200px, 1fr) auto"
          >
            <Header>
              <Row>
                <Column>
                  <Trans id="settings.external-stores.column.name">Name</Trans>
                </Column>
                <Column>
                  <Trans id="settings.external-stores.column.address">Address</Trans>
                </Column>
                <Column>
                  <Trans id="settings.external-stores.column.contact">Contact</Trans>
                </Column>
                <Column
                  ariaLabel={t({
                    id: 'settings.external-stores.column.actions',
                    message: 'Actions',
                  })}
                />
              </Row>
            </Header>
            <Body>
              {stores?.map((store) => (
                <ExternalStoreRow key={store.id} store={store} />
              ))}
            </Body>
            <PaginationFooter
              page={page}
              itemsPerPage={STORES_PER_PAGE}
              count={meta?.count}
              onPageChange={setPage}
            />
          </Table>
        )}
      </Box>
    </Stack>
  );
};

const ExternalStoreRow = ({ store }: { store: StoreFromGetMany }) => {
  const { t } = useLingui();

  const { mutateAsync: deleteStore, isPending: isDeleteStorePending } = useDeleteStore();

  const accessCode = store.organizationConfig?.accessCode;

  const label = t({
    id: 'settings.external-stores.delete.tooltip',
    message: `Remove ${store.name}`,
  });

  return (
    <Row>
      <Cell>
        <Stack>
          <b>
            {store.name}
            {store.organizationConfig?.externalId && <> ({store.organizationConfig?.externalId})</>}
          </b>
          {store.groups.length > 0 && (
            <small>({store.groups.map((group) => group.name).join(', ')})</small>
          )}
          {accessCode && (
            <small>
              <Trans id="settings.external-stores.access-code">
                After-sales code: {accessCode}
              </Trans>
            </small>
          )}
        </Stack>
      </Cell>
      <Cell>
        <p>
          {store.address ? (
            <>
              {formatStreet(store.address) ?? '-'}
              <br />
              {formatZip(store.address) ?? '-'}
            </>
          ) : (
            '-'
          )}
        </p>
      </Cell>
      <Cell>
        <p>
          {store.address?.contactName ?? '-'}
          <br />
          {store.address?.contactEmail ?? '-'}
          <br />
          {formatPhone(store.contactInfo?.phone) ?? '-'}
        </p>
      </Cell>
      <Cell justifyContent="flex-end">
        <Stack row gap="4px">
          <UpdateExternalStore store={store} />
          <Button
            variant="secondary"
            iconOnly
            size="small"
            ariaLabel={label}
            tooltip={label}
            isLoading={isDeleteStorePending}
            disabled={isDeleteStorePending}
            onPress={() => {
              if (
                confirm(
                  t({
                    id: 'settings.external-stores.delete.confirm-dialog',
                    message: `Are you sure you want to remove the ${store.name} store from your organization?`,
                  })
                )
              ) {
                deleteStore(store.id).catch(console.error);
              }
            }}
          >
            <IconTrash />
          </Button>
        </Stack>
      </Cell>
    </Row>
  );
};

const UpdateExternalStore = ({ store }: { store: StoreFromGetMany }) => {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);

  const label = t({
    id: 'settings.external-stores.edit-modal.trigger',
    message: `Edit ${store.name}`,
  });

  return (
    <Dialog
      isOpen={open}
      onOpenChange={setOpen}
      trigger={
        <Button variant="secondary" iconOnly size="small" ariaLabel={label} tooltip={label}>
          <IconEdit />
        </Button>
      }
      title={t({ id: 'settings.external-stores.edit-modal.title', message: `Edit ${store.name}` })}
    >
      <UpdateExternalStoreContent store={store} setOpen={setOpen} />
    </Dialog>
  );
};

const UpdateExternalStoreContent = ({
  store,
  setOpen,
}: {
  store: StoreFromGetMany;
  setOpen: (open: boolean) => void;
}) => {
  const [organization] = useCurrentOrganization();

  const showCustomFields = (organization?.config?.customFields?.store?.length || 0) > 0;

  const [groups, setGroups] = useState(store.groups ?? []);
  const [externalId, setExternalId] = useState(store.organizationConfig?.externalId);
  const [customFields, setCustomFields] = useState<CustomFieldsType>(
    store?.organizationConfig?.customFields ?? {}
  );

  const { mutateAsync: updateStore, isPending: isUpdateStorePending } = useUpdateStore();

  return (
    <>
      <main style={{ maxHeight: '70vh' }}>
        <Stack gap="24px">
          <InputText
            label={
              <Trans id="settings.external-stores.edit-modal.field.name.label">
                Name of the store
              </Trans>
            }
            type="text"
            value={store.name}
            isDisabled={true}
          />

          <GroupsSelector groups={groups} setGroups={setGroups} />

          <InputText
            label={
              <Trans id="settings.external-stores.edit-modal.field.external-id.label">
                External ID
              </Trans>
            }
            value={externalId ?? ''}
            onChange={setExternalId}
            isDisabled={isUpdateStorePending}
          />

          <InputLocale value={store.defaultLocale} isDisabled={true} />

          {showCustomFields && (
            <CustomFieldsGroup
              fields={organization?.config?.customFields?.store ?? []}
              customFields={customFields}
              setCustomFields={setCustomFields}
            />
          )}
        </Stack>
      </main>
      <Footer>
        <FooterButtons>
          <Button
            variant="primary"
            size="small"
            type="submit"
            isLoading={isUpdateStorePending}
            onPress={() =>
              updateStore({
                storeId: store.id,
                groups: groups?.map((group) => ({ id: group.id })),
                externalId: externalId || null,
                customFields: showCustomFields ? customFields : undefined,
              }).then(() => setOpen(false))
            }
          >
            <Trans id="settings.external-stores.edit-modal.submit">Update store</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
